import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import { useMediaQuery } from "@mantine/hooks";

import { ViewerPage } from "../viewer/ViewerPage";
import { SampleGrid } from "./SampleGrid";
import { StampDetailSidebar } from "./StampDetailSidebar";
import stampData from "./stamps.json";

type ViewMode = "grid" | "cloud";

const EASE_OUT_CUBIC = [0.215, 0.61, 0.355, 1] as const;

const viewVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.15, ease: EASE_OUT_CUBIC } },
  exit: { opacity: 0, transition: { duration: 0.12, ease: EASE_OUT_CUBIC } },
};

const allKeys = Object.keys(stampData).sort();
const allCountries = Array.from(
  new Set(allKeys.map((k) => (stampData as Record<string, any>)[k].country as string))
).sort();

export function SamplePage() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [view, setView] = React.useState<ViewMode>("cloud");
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCountries, setActiveCountries] = React.useState<Set<string>>(new Set());
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  const selectedId = searchParams.get("stamp") ?? null;

  // Force 2D view on mobile
  const effectiveView: ViewMode = isMobile ? "grid" : view;

  const filteredKeys = React.useMemo(() =>
    activeCountries.size === 0
      ? allKeys
      : allKeys.filter((k) =>
          activeCountries.has((stampData as Record<string, any>)[k].country)
        ),
    [activeCountries]
  );

  const handleSelect = React.useCallback(
    (id: string) => setSearchParams({ stamp: id }),
    [setSearchParams]
  );
  const handleClose = React.useCallback(
    () => setSearchParams({}),
    [setSearchParams]
  );
  const handleNavigate = React.useCallback(
    (id: string) => setSearchParams({ stamp: id }),
    [setSearchParams]
  );
  const toggleCountry = React.useCallback((country: string) => {
    setActiveCountries((prev) => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country);
      else next.add(country);
      return next;
    });
  }, []);

  return (
    <div style={{ display: "flex", height: "calc(100vh - 60px)", overflow: "hidden", position: "relative" }}>

      {/* Left column: controls strip + view */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>

        {/* Controls strip — sits above cloud/grid, never overlaps */}
        <div style={{
          padding: isMobile ? "0.6rem 1rem" : "0.75rem 1.5rem",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          borderBottom: "1px solid #e27130",
        }}>
          {/* View toggle — hidden on mobile (2D only) */}
          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontFamily: "IM Fell English, serif", fontSize: "12px", color: "#9b9974", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                view
              </span>
              <div style={{ display: "flex", gap: "6px" }}>
                <ChipButton active={view === "cloud"} onClick={() => setView("cloud")}>3d</ChipButton>
                <ChipButton active={view === "grid"} onClick={() => setView("grid")}>2d</ChipButton>
              </div>
            </div>
          )}
          {/* Country filter — wrapping on desktop, single horizontal scroll row on mobile */}
          <div style={{ display: "flex", alignItems: isMobile ? "center" : "flex-start", gap: "10px", minWidth: 0 }}>
            <span style={{
              fontFamily: "IM Fell English, serif",
              fontSize: "12px",
              color: "#9b9974",
              letterSpacing: "0.06em",
              paddingTop: isMobile ? 0 : "5px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}>
              country
            </span>
            <div
              className="country-chips-row"
              style={{
                display: "flex",
                flexWrap: isMobile ? "nowrap" : "wrap",
                gap: "6px",
                overflowX: isMobile ? "auto" : "visible",
                paddingBottom: isMobile ? "2px" : 0,
                minWidth: 0,
                ...(isMobile ? { WebkitOverflowScrolling: "touch", scrollbarWidth: "none" } : {}),
              }}
            >
              <ChipButton active={activeCountries.size === 0} onClick={() => setActiveCountries(new Set())} small={isMobile}>
                all
              </ChipButton>
              {allCountries.map((country) => (
                <ChipButton
                  key={country}
                  active={activeCountries.has(country)}
                  onClick={() => toggleCountry(country)}
                  small={isMobile}
                >
                  {country}
                </ChipButton>
              ))}
            </div>
          </div>
        </div>

        {/* View — fills all remaining height */}
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <AnimatePresence mode="wait">
            {effectiveView === "grid" ? (
              <motion.div key="grid" variants={viewVariants} initial="initial" animate="animate" exit="exit" style={{ height: "100%" }}>
                <SampleGrid onSelect={handleSelect} selectedId={selectedId} filteredKeys={filteredKeys} isMobile={isMobile} />
              </motion.div>
            ) : (
              <motion.div key="cloud" variants={viewVariants} initial="initial" animate="animate" exit="exit" style={{ height: "100%" }}>
                <ViewerPage
                  onSelect={handleSelect}
                  onHover={setHoveredId}
                  selectedId={selectedId ?? undefined}
                  filteredKeys={filteredKeys}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop: persistent 420px side column */}
      {!isMobile && (
        <div style={{
          flexShrink: 0,
          width: 420,
          height: "100%",
          overflow: "hidden",
          borderLeft: "1px solid #e27130",
        }}>
          <AnimatePresence mode="wait">
            {(selectedId || hoveredId) ? (
              <motion.div
                key={selectedId ?? hoveredId}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.15, ease: EASE_OUT_CUBIC } }}
                exit={{ opacity: 0, transition: { duration: 0.1, ease: EASE_OUT_CUBIC } }}
                style={{ width: "100%", height: "100%" }}
              >
                <StampDetailSidebar
                  stampId={selectedId ?? hoveredId!}
                  onClose={handleClose}
                  onNavigate={handleNavigate}
                  showClose={!!selectedId}
                />
              </motion.div>
            ) : (
              <motion.div
                key="grid-panel"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.2, ease: EASE_OUT_CUBIC } }}
                exit={{ opacity: 0, transition: { duration: 0.15, ease: EASE_OUT_CUBIC } }}
                style={{ width: "100%", height: "100%", overflowY: "auto" }}
              >
                <SidebarGrid filteredKeys={filteredKeys} onSelect={handleSelect} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Mobile: stamp detail slides in from right as full-screen overlay (covers header) */}
      {isMobile && (
        <AnimatePresence>
          {selectedId && (
            <motion.div
              key="mobile-overlay"
              initial={{ x: "100%" }}
              animate={{ x: 0, transition: { duration: 0.28, ease: EASE_OUT_CUBIC } }}
              exit={{ x: "100%", transition: { duration: 0.22, ease: EASE_OUT_CUBIC } }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "#fff",
                zIndex: 10000,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <button
                onClick={handleClose}
                style={{
                  alignSelf: "flex-start",
                  padding: "1.75rem 2rem 0.75rem",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9b9974",
                  fontFamily: "IM Fell English, serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: "10px",
                }}
              >
                ← back
              </button>
              <div style={{ flex: 1, minHeight: 0 }}>
                <StampDetailSidebar
                  stampId={selectedId}
                  onClose={handleClose}
                  onNavigate={handleNavigate}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}

function SidebarGrid({
  filteredKeys,
  onSelect,
}: {
  filteredKeys: string[];
  onSelect: (id: string) => void;
}) {
  return (
    <div style={{ padding: "1.25rem 1.5rem" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "8px",
        }}
      >
        {filteredKeys.map((key) => (
          <button
            key={key}
            onClick={() => onSelect(key)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              borderRadius: "4px",
              transition: "opacity 150ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <img
              src={`https://vidyagiri.com/stamps/img/${key}.png`}
              alt={`stamp ${key}`}
              loading="lazy"
              style={{ width: "100%", aspectRatio: "1", objectFit: "contain" }}
            />
            <span style={{ fontFamily: "IM Fell English, serif", fontSize: "11px", color: "#9b9974" }}>
              {key}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipButton({
  active,
  onClick,
  children,
  small,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  small?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: small ? "2px 10px" : "4px 14px",
        border: "1px solid",
        borderColor: active ? "#e27130" : "rgba(155,153,116,0.4)",
        borderRadius: "999px",
        background: active ? "#e27130" : "transparent",
        color: active ? "#fff" : "#9b9974",
        fontFamily: "IM Fell English, serif",
        fontSize: small ? "12px" : "14px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        flexShrink: 0,
        transition: "background 150ms ease, color 150ms ease, border-color 150ms ease",
      }}
    >
      {children}
    </button>
  );
}
